import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { LearnerSettingsPage } from './LearnerSettingsPage';
import { LEARNER_SETTINGS_MODAL_CLOSED_EVENT } from '../../constants/learnerPersianFonts';
import { LEARNER_SETTINGS_MODAL_CHROME_CLASS } from './learnerSettingsSectionThemes';
import { FLOATING_KEYBOARD_ANIM_MS, MODAL_BACKDROP_FADE_MS } from '../../utils/learnerSlideModalMotion';
import { prefersReducedMotion } from '../../utils/subscriptionAccessibility';
import { ModalTourHelpButton } from '../tour/ModalTourHelpButton';
import { emitLearnerTourOverlayPageKey } from '../../contexts/ProductTourContext';
import { quickSettingsTourPageKey } from '../../learnerTour/learnerTourRegistry';
import { useLearnerHomeDesign } from '../../utils/learnerHomeDesign';
import { useHistoryBackClose } from '../../hooks/useHistoryBackClose';
import { useAppLocale } from '../../providers/LocaleProvider';
import type { LearnerSettingsModalTabId } from './LearnerSettingsModalTabs';

const SETTINGS_MODAL_CLOSE_ANIM_MS = Math.max(FLOATING_KEYBOARD_ANIM_MS, MODAL_BACKDROP_FADE_MS);

export interface LearnerSettingsModalProps {
  open: boolean;
  onClose: () => void;
  onOpenProfileGoals?: () => void;
  initialTab?: LearnerSettingsModalTabId;
  focusInAppAlerts?: boolean;
  focusRequestId?: number;
}

/**
 * Quick learner settings modal — same motion as Keep Learning:
 * slide up on mobile, from the side banner on desktop (right in EN, left in FA).
 */
export const LearnerSettingsModal: React.FC<LearnerSettingsModalProps> = ({
  open,
  onClose,
  onOpenProfileGoals,
  initialTab,
  focusInAppAlerts,
  focusRequestId,
}) => {
  const { t } = useTranslation('settings');
  const { t: tCommon } = useTranslation('common');
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const [homeDesign] = useLearnerHomeDesign();
  const [activeTab, setActiveTab] = useState<LearnerSettingsModalTabId>(initialTab ?? 'security');
  const tourPageKey = quickSettingsTourPageKey(homeDesign, activeTab);
  const panelRef = useRef<HTMLDivElement>(null);
  const isClosingRef = useRef(false);
  const finishCloseRef = useRef(false);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  const finishClose = useCallback(() => {
    if (finishCloseRef.current) return;
    finishCloseRef.current = true;
    isClosingRef.current = false;
    window.dispatchEvent(new CustomEvent(LEARNER_SETTINGS_MODAL_CLOSED_EVENT));
    onClose();
    setMounted(false);
  }, [onClose]);

  const requestClose = useCallback(() => {
    if (finishCloseRef.current || isClosingRef.current) return;
    isClosingRef.current = true;
    setVisible(false);
    if (!visible) {
      window.requestAnimationFrame(() => {
        if (!finishCloseRef.current) finishClose();
      });
    }
  }, [visible, finishClose]);

  const onHistoryBack = useCallback(() => {
    if (finishCloseRef.current || isClosingRef.current) return true;
    requestClose();
    return false;
  }, [requestClose]);

  useHistoryBackClose(open, onHistoryBack, 'learner-settings');

  // Mount / open / start close when parent toggles open
  useLayoutEffect(() => {
    if (open) {
      finishCloseRef.current = false;
      isClosingRef.current = false;
      setMounted(true);
      setVisible(prefersReducedMotion());
      return undefined;
    }
    isClosingRef.current = true;
    setVisible(false);
    return undefined;
  }, [open]);

  // Paint the hidden panel first so the enter transition runs (same classes as Keep Learning).
  useLayoutEffect(() => {
    if (!open || !mounted || visible || isClosingRef.current) return undefined;
    if (prefersReducedMotion()) {
      setVisible(true);
      return undefined;
    }
    const panel = panelRef.current;
    if (panel) void panel.offsetHeight;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (!isClosingRef.current) setVisible(true);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [open, mounted, visible]);

  const handleExitTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      if (e.propertyName !== 'opacity') return;
      if (visible || !isClosingRef.current) return;
      finishClose();
    },
    [visible, finishClose]
  );

  // Fallback if transitionend does not fire
  useEffect(() => {
    if (visible || !mounted || !isClosingRef.current) return undefined;
    const t = window.setTimeout(finishClose, SETTINGS_MODAL_CLOSE_ANIM_MS + 50);
    return () => window.clearTimeout(t);
  }, [visible, mounted, finishClose]);

  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mounted, requestClose]);

  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (!open || !visible) return undefined;
    return () => emitLearnerTourOverlayPageKey(null, false);
  }, [open, visible]);

  useEffect(() => {
    if (open && visible) emitLearnerTourOverlayPageKey(tourPageKey, true);
  }, [open, visible, tourPageKey]);

  if (!mounted) return null;

  return (
    <div className="learner-slide-modal-root fixed inset-0 z-[200] py-6 sm:py-10" role="presentation">
      <div
        className={`keyboard-slide-modal-backdrop fixed inset-0 bg-black/45 backdrop-blur-[2px] dark:bg-black/55${
          visible ? ' is-visible' : ''
        }`}
        aria-hidden="true"
        onClick={requestClose}
      />
      <div className="flex min-h-full items-end justify-center px-3 sm:items-center sm:px-6">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="learner-settings-modal-title"
          className={`keyboard-slide-modal-panel keyboard-slide-modal-panel--from-banner mme-tone pointer-events-auto relative flex h-[min(85vh,780px)] w-[min(100%,26rem)] max-w-lg flex-col overflow-hidden rounded-t-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-slate-100 shadow-2xl dark:border-gray-600 dark:from-gray-900 dark:to-gray-950 sm:w-full sm:max-w-2xl sm:rounded-2xl${
            visible ? ' is-visible' : ''
          }`}
          onTransitionEnd={handleExitTransitionEnd}
          onClick={(e) => e.stopPropagation()}
        >
            <div
              className={`flex shrink-0 items-start justify-between gap-2 border-b px-3 py-2 sm:px-4 ${LEARNER_SETTINGS_MODAL_CHROME_CLASS}`}
            >
              <div className="min-w-0 py-0.5">
                <h2 id="learner-settings-modal-title" className="text-start text-base font-semibold text-slate-900 dark:text-white">
                  {t('quickSettingsTitle')}
                </h2>
                <p className="mt-1 text-start text-sm text-gray-600 dark:text-gray-400">
                  {t('quickSettingsSubtitle', {
                    defaultValue: isRtl
                      ? 'امنیت، اعلان‌ها، متن درس، منطقه و ترجیحات.'
                      : 'Security, notifications, lesson text, region, and preferences.',
                  })}
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-center gap-0">
                <button
                  type="button"
                  onClick={requestClose}
                  className="rounded-lg p-1 text-slate-600 transition-colors hover:bg-slate-200/80 dark:text-slate-300 dark:hover:bg-gray-700"
                  aria-label={t('closeSettingsAria')}
                  title={tCommon('close')}
                >
                  <X className="h-4 w-4" />
                </button>
                <ModalTourHelpButton tourPageKey={tourPageKey} />
              </div>
            </div>
            <div className="flex min-h-0 flex-1 flex-col">
              <LearnerSettingsPage
                variant="modal"
                onClose={requestClose}
                onActiveTabChange={setActiveTab}
                {...(onOpenProfileGoals ? { onOpenProfileGoals } : {})}
                {...(initialTab ? { initialTab } : {})}
                {...(focusInAppAlerts ? { focusInAppAlerts } : {})}
                {...(focusRequestId ? { focusRequestId } : {})}
              />
            </div>
        </div>
      </div>
    </div>
  );
};
